import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface InAppNotification {
  id: string; // crypto.randomUUID()
  title: string;
  body: string;
  /** Payload data FCM (ex. référence transaction, montant). */
  data?: Record<string, string>;
  receivedAt: string; // ISO
  read: boolean;
}

/** Rétention locale : on ne garde que les 50 notifications les plus récentes. */
const MAX_NOTIFICATIONS = 50;

interface NotificationState {
  notifications: InAppNotification[];
  fcmToken: string | null;
  permission: NotificationPermission | "unsupported";
  addNotification: (
    n: Pick<InAppNotification, "title" | "body" | "data">
  ) => void;
  markRead: (id: string) => void;
  markUnread: (id: string) => void;
  markAllRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  setFcmToken: (token: string | null) => void;
  setPermission: (p: NotificationPermission | "unsupported") => void;
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set) => ({
      notifications: [],
      fcmToken: null,
      permission: "default",
      addNotification: (n) =>
        set((s) => ({
          notifications: [
            {
              ...n,
              id: crypto.randomUUID(),
              receivedAt: new Date().toISOString(),
              read: false,
            },
            ...s.notifications,
          ].slice(0, MAX_NOTIFICATIONS),
        })),
      markRead: (id) =>
        set((s) => ({
          notifications: s.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
        })),
      markUnread: (id) =>
        set((s) => ({
          notifications: s.notifications.map((n) =>
            n.id === id ? { ...n, read: false } : n
          ),
        })),
      markAllRead: () =>
        set((s) => ({
          notifications: s.notifications.map((n) => ({ ...n, read: true })),
        })),
      removeNotification: (id) =>
        set((s) => ({
          notifications: s.notifications.filter((n) => n.id !== id),
        })),
      clearAll: () => set({ notifications: [] }),
      setFcmToken: (fcmToken) => set({ fcmToken }),
      setPermission: (permission) => set({ permission }),
    }),
    {
      name: "flash-pay-notifications",
      // La permission est relue à chaque démarrage (elle peut changer dans
      // les réglages du navigateur) : seules les notifications et le token
      // sont persistés.
      partialize: (s) => ({ notifications: s.notifications, fcmToken: s.fcmToken }),
    }
  )
);

export const selectUnreadCount = (s: NotificationState) =>
  s.notifications.filter((n) => !n.read).length;
